import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import Select from 'react-select';
import Modal from 'react-modal';
import axios from 'axios';
import moment from 'moment-timezone';
import { useTranslation } from 'react-i18next';
import { ApiBaseUrlContext } from '../App';
import EditRights from './editRights';
import './Settings.css';
import '../boxitem.css';
import '../Button.css';
import '../forms.css';
import '../filter.css';
import '../topmenu.css';
import { ReactComponent as RemoveItemIcon } from '../assets/svg/icon-remove-1.svg';
import { ReactComponent as EditItemIcon } from '../assets/svg/icon-edit.svg';
import { ReactComponent as AddIcon } from '../assets/svg/icon-add.svg';

const Settings = ({ setApiBaseUrl }) => {
  const apiBaseUrl = useContext(ApiBaseUrlContext);
  const { t, i18n } = useTranslation();
  const textareaRefs = useRef([]);

  // Settings states
  const [licenseKey, setLicenseKey] = useState('');
  const [originalLicenseKey, setOriginalLicenseKey] = useState('');
  const [isLicenseEditing, setIsLicenseEditing] = useState(false);
  
  const [licenseInfo, setLicenseInfo] = useState(null); // Store license details here
  const [licenseInfoValid, setLicenseInfoValid] = useState(null);

															   
  const [serverIp, setServerIp] = useState('');
  const [port, setPort] = useState('5050');
  const [timezone, setTimezone] = useState('');
  const [requiredFields, setRequiredFields] = useState({ machine: true, fault: true, technician: true });
  const [uploadPath, setUploadPath] = useState('');
  const [hourlyRate, setHourlyRate] = useState(0.00);
  const [upcomingStatusTimeDays, setUpcomingStatusTimeDays] = useState(1);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [language, setLanguage] = useState(i18n.language);
  const [cimcoApiBaseUrl, setCimcoApiBaseUrl] = useState('');
  

  // Email settings
  const [emailHost, setEmailHost] = useState('');
  const [emailPort, setEmailPort] = useState(465);
  const [emailUser, setEmailUser] = useState('');
  const [emailPass, setEmailPass] = useState('');

  // Categories
  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState('');
  const [editCategory, setEditCategory] = useState('');
  const [editIndex, setEditIndex] = useState(null);
  const [currencySymbol, setCurrencySymbol] = useState('');
  const [currencyPosition, setCurrencyPosition] = useState('left');
  const [dbPass, setDbPass] = useState('');
  const [maxFileSizeMB, setMaxFileSizeMB] = useState('');
  const [registrationCode, setRegistrationCode] = useState('');
  // Add this near your other state declarations:
  const [versionData, setVersionData] = useState(null);
  const [showAllVersions, setShowAllVersions] = useState(false);
  // Tab control
  const [activeTab, setActiveTab] = useState('general');
  const [settingsTitle, setSettingsTitle] = useState(t('settingsPage.btnGeneralSettings'));
  const [emailPresets, setEmailPresets] = useState([
    { id: 1, title: 'Fault Email', body: '' },
    { id: 2, title: 'Maintenance Email', body: '' },
    { id: 3, title: 'Inventory reminder Email', body: '' },
    { id: 3, title: 'Inventory new Email', body: '' }
  ]);
  

  const handleMenuButtonClick = (tab, title) => {
    setActiveTab(tab);
    setSettingsTitle(title);
  };

  const toggleShowAllVersions = () => {
    setShowAllVersions((prev) => !prev);
  };

  // Mobile select dropdown handler
  const handleSelectChange = (event) => {
    const selectedTab = event.target.value;
    const selectedTitle = optionList.find(option => option.key === selectedTab)?.label || "";

    handleMenuButtonClick(selectedTab, selectedTitle);
  };

  // Option list now includes a new license tab
  const optionList = [
    { key: "general", label: t("settingsPage.btnGeneralSettings") },
    { key: "fields", label: t("settingsPage.btnServiceReports") },
    { key: "categories", label: t("settingsPage.btnCategories") },
    { key: "editRights", label: t("settingsPage.btnPermissions") },
    { key: "email", label: t("settingsPage.btnEmail") },
    { key: "advanced", label: t("settingsPage.btnAdvanced") },
    { key: "license", label: t("settingsPage.btnLicenseInformation") } // New Tab
  ];
  

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${apiBaseUrl}/mnt/api/settings`);
        const settings = response.data;

        setServerIp(settings.serverIp || '');
        setPort(settings.port || '');
        setTimezone(settings.timezone || '');
        setRequiredFields(settings.requiredFields || { machine: true, fault: true, technician: true });
        setUploadPath(settings.uploadPath || '');
        setHourlyRate(settings.hourlyRate || 0.00);
        setUpcomingStatusTimeDays(settings.upcomingStatusTimeDays || 1);
        setCategories(settings.categories || []);
        setCimcoApiBaseUrl(settings.CIMCO_API_BASE_URL || 'http://localhost:400/mdc/api/v1');
        setLicenseKey(settings.licenseKey || '');
        setOriginalLicenseKey(settings.licenseKey || '');
        setCurrencySymbol(settings.currencySymbol || 'kr');
        setCurrencyPosition(settings.currencyPosition || 'right');
        setMaxFileSizeMB(settings.maxFileSizeMB || '');
        setRegistrationCode(settings.registrationCode || '');
																										
									   
      } catch (error) {
        console.error('Error fetching settings:', error);
      }
    };

    const fetchEmailSettings = async () => {
      try {
        const response = await axios.get(`${apiBaseUrl}/mnt/api/email/settings`);
        const settings = response.data;
    
        setEmailHost(settings.EMAIL_HOST || '');
        setEmailPort(settings.EMAIL_PORT || 465);
        setEmailUser(settings.EMAIL_USER || '');
        setEmailPass(settings.EMAIL_PASS || '');
    
        if (Array.isArray(settings.emailPresets)) {
          const mappedPresets = [
            {
              id: null,
              title: 'Fault Email',
              body: '',
            },
            {
              id: null,
              title: 'Maintenance Email',
              body: '',
            },
            {
              id: null,
              title: 'Inventory reminder Email',
              body: '',
            },
            {
              id: null,
              title: 'Inventory new Email',
              body: '',
            },
          ];
    
          // Match presets by name to set into correct slots
          settings.emailPresets.forEach(preset => {
            if (preset.name === 'Fault Email') {
              mappedPresets[0] = { id: preset.id, title: preset.name, body: preset.email_content };
            } else if (preset.name === 'Maintenance Email') {
              mappedPresets[1] = { id: preset.id, title: preset.name, body: preset.email_content };
            } else if (preset.name === 'Inventory reminder Email') {
              mappedPresets[2] = { id: preset.id, title: preset.name, body: preset.email_content };
            }else if (preset.name === 'Inventory new Email') {
              mappedPresets[3] = { id: preset.id, title: preset.name, body: preset.email_content };
            }
          });
    
          setEmailPresets(mappedPresets);
        }
    
      } catch (error) {
        console.error('Error fetching email settings:', error);
      }
    };
    



    fetchSettings();
    fetchEmailSettings();

  }, [apiBaseUrl, setApiBaseUrl]);

// Then add a useEffect to fetch the version info:
useEffect(() => {
  axios.get('/mnt/api/version')
    .then(response => setVersionData(response.data))
    .catch(error => console.error('Error loading version data:', error));
}, []);



  const maskLicense = (key) => {
    if (!key) return '';
    if (key.length <= 5) return key;
    // Mask all but the last 5 characters. You can adjust the number of groups/dashes as needed.
    return '*****-*****-*****-' + key.slice(-5);
  };
  // Fetch license information from Cryptolens API
  const fetchLicenseInformation = useCallback(async () => {
    if (!licenseKey) return; // Only fetch if license key exists
    try {
      const token = "WyIxMDI5Mzg4NTEiLCJhRTEyN09FSVBvemxvVmVhSmZXL3NHR1h4b3hTNHNJcXNPRWZkRFRVIl0=";
      const ProductId = 27652;
      const response = await axios.get(`https://app.cryptolens.io/api/key/GetKey`, {
        params: {
          token,
          ProductId,
          Key: licenseKey,
        },
      });
      // The API returns license data under the "licenseKey" property.
      if (response.data && response.data.licenseKey) {
        setLicenseInfo(response.data.licenseKey);
      } else {
        console.error('Invalid license data', response.data);
      }
    } catch (error) {
      console.error("Error fetching license information", error);
    }
  }, [licenseKey]);

  // When the license tab becomes active, fetch license info.
  useEffect(() => {
    if (activeTab === 'license') {
      fetchLicenseInformation();
    }
  }, [activeTab, fetchLicenseInformation]);


  //Logo upload 20250306
  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
  
    if (!file) return;
  
    // Check file type
    if (file.type !== "image/png") {
      alert("Only PNG files are allowed.");
      return;
    }
  
    const formData = new FormData();
    formData.append("logo", file);
  
    try {
      const response = await axios.post(`${apiBaseUrl}/mnt/api/logo/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
  
      alert(response.data.message);
    } catch (error) {
      console.error("Error uploading logo:", error);
      alert("Failed to upload logo.");
    }
  };
  


  // Save settings (including license validation)
  const handleSave = async () => {
    try {
																 
      const licenseResponse = await axios.post(`${apiBaseUrl}/mnt/api/licenses/validate`, { licenseKey });
      if (licenseResponse.data.valid) {
        setLicenseInfoValid(licenseResponse.data.licenseInfo);
        alert('License validated and settings saved successfully.');
      } else {
        alert('Invalid license key. Please check your key and try again.');
        return;
      }
      const settings = {
        serverIp,
        port,
        timezone,
        requiredFields,
        uploadPath,
        hourlyRate,
        upcomingStatusTimeDays,
        categories,
        CIMCO_API_BASE_URL: cimcoApiBaseUrl,
        licenseKey,
        currencySymbol,
        currencyPosition,
        dbPass,
        maxFileSizeMB,
        registrationCode
      };
      localStorage.setItem('timezone', timezone);
      try {
        await axios.post(`${apiBaseUrl}/mnt/api/settings`, settings);
														   
									 
        setModalMessage(t('settingsPage.saveSuccess'));
        setModalIsOpen(true);
      } catch (error) {
        console.error('Error saving settings:', error);
        setModalMessage(t('settingsPage.saveError'));
        setModalIsOpen(true);
      }
    } catch (error) {
      console.error('Error saving settings:', error);
    }
  };

  
const handleAdvanced = () =>{
  fetchLicesnseData();
  setActiveTab('advanced');
}
const fetchLicesnseData = async () => {
  try{
    const licenseResponse = await axios.post(`${apiBaseUrl}/mnt/api/licenses/validate`, { licenseKey });
    if (licenseResponse.data.valid) {
      setLicenseInfoValid(licenseResponse.data.licenseInfo);
    } else {
    }
  }catch{

  }
};
  const handleAddCategory = () => {
    if (newCategory.trim()) {
      setCategories([...categories, newCategory.trim()]);
      setNewCategory('');
    }
  };

  const handleEditCategory = (index) => {
    setEditCategory(categories[index]);
    setEditIndex(index);
  };

  const handleUpdateCategory = () => {
    if (editCategory.trim()) {
      const updatedCategories = categories.map((cat, idx) => (idx === editIndex ? editCategory.trim() : cat));
      setCategories(updatedCategories);
      setEditCategory('');
      setEditIndex(null);
    }
  };

  const handleDeleteCategory = (index) => {
    const confirmDelete = window.confirm(t('settingsPage.confirmDelete'));
    if (confirmDelete) {
      setCategories(categories.filter((_, idx) => idx !== index));
    }
  };


  /*const handleUpdateInternalRates = async () => {
    try {
      const response = await axios.post(`${apiBaseUrl}/mnt/api/settings/internalRates`, { hourlyRate });
      setModalMessage(response.data.message || t('settingsPage.updateRatesSuccess'));
    } catch (error) {
      console.error('Error updating internal hourly rates:', error);
      setModalMessage(t('settingsPage.updateRatesError'));
    }
    setModalIsOpen(true);
  };*/

  // Handle language change
  const handleLanguageChange = (selectedOption) => {
    setLanguage(selectedOption.value); // Update language in state
    i18n.changeLanguage(selectedOption.value); // Change language in i18next
    localStorage.setItem('language', selectedOption.value); // Persist language in localStorage
  };
  // Save email settings
  const handleSaveEmailSettings = async () => {
    const emailSettings = {
      EMAIL_HOST: emailHost,
      EMAIL_PORT: emailPort,
      EMAIL_USER: emailUser,
      EMAIL_PASS: emailPass,
      emailPresets: emailPresets.map((preset) => ({
        id: preset.id || undefined, // optional, let backend distinguish create vs update
        name: preset.title,
        email_content: preset.body,
      })),
    };
  
    try {
      await axios.post(`${apiBaseUrl}/mnt/api/email/settings`, emailSettings);
      setModalMessage(t('settingsPage.saveSuccess'));
      setModalIsOpen(true);
    } catch (error) {
      console.error('Error saving email settings:', error);
      setModalMessage(t('settingsPage.saveError'));
      setModalIsOpen(true);
    }
  };
  

  // Language options and handler
  const languageOptions = [
    { value: 'en', label: 'English' },
    { value: 'sv', label: 'Swedish' },
    { value: 'dk', label: 'Danish' },
    { value: 'de', label: 'German' },
    { value: 'it', label: 'Italian' },
    { value: 'pt', label: 'Portuguese' },
    { value: 'fr', label: 'French' },
    { value: 'es', label: 'Spanish' },
    { value: 'cn', label: 'Chinese' },
    { value: 'pl', label: 'Polish' },
    { value: 'cz', label: 'Czech' },
    { value: 'hu', label: 'Hungarian' },
    { value: 'ee', label: 'Estonian' },
    { value: 'fi', label: 'Finnish' },
  ];


  const insertAtCursor = (text, index) => {
    const ref = textareaRefs.current[index];
    if (!ref) return;
  
    const textarea = ref;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const value = emailPresets[index].body;
  
    const newValue = value.slice(0, start) + text + value.slice(end);
  
    const updated = [...emailPresets];
    updated[index].body = newValue;
    setEmailPresets(updated);
  
    // Put the cursor after the inserted text
    setTimeout(() => {
      textarea.focus();
      textarea.selectionStart = textarea.selectionEnd = start + text.length;
    }, 0);
  };
  const getPresetVariables = (presetId) => {
    switch (presetId) {
      case 1: // Fault report
        return ['machine', 'description', 'priority', 'technician'];
      case 2: // Maintenance reminder
        return ['machineid', 'maintenance_type', 'next_maintenance_date'];
      case 3: // Inventory reminder (maintenance/calibration)
        return ['item_name', 'type', 'status', 'status_text', 'date_phrase', 'date'];
      case 4: // New inventory assignment
        return ['item_name', 'category', 'model_number', 'manufacturer', 'purchase_date'];
      default:
        return [];
    }
  };
  
  const insertVariable = (variable, index) => {
    insertAtCursor(`{${variable}}`, index);
  };
  
  const insertLink = (index) => {
    insertAtCursor(`<a href="https://your-link.com">Link text</a>`, index);
  };
  
  // Timezone options
  const timezones = moment.tz.names().map(tz => ({ value: tz, label: tz }));

  // Render functions for each tab
									   

  const renderGeneralSettings = () => (
    <>

      <div className="form-section">

        <div className="entry-item entry-item__content-width">
          <label htmlFor="language" className="entry-label">{t('settingsPage.language')}</label>
          <Select
            id="language"
            value={languageOptions.find((opt) => opt.value === language)}
            onChange={handleLanguageChange}
            options={languageOptions}
            className="entry-select"
            classNamePrefix="entry-select"
          />
        </div>

        <div className="entry-item entry-item__small">
          <label htmlFor="currencySymbol" className="entry-label">{t('settingsPage.currencySymbol')}</label>
          <input
            type="text"
            id="currencySymbol"
            className="entry-control"
            value={currencySymbol}
            onChange={(e) => setCurrencySymbol(e.target.value)}
            placeholder="Enter currency symbol (e.g., kr)"
          />
        </div>

        <div className="entry-item">
          <label className="entry-label">{t('settingsPage.currencyPosition')}</label>
          <div className="entry-item__input-and-label">
            <input
              className="entry-control"
              type="radio"
              id="positionLeft"
              checked={currencyPosition === 'left'}
              onChange={() => setCurrencyPosition('left')}
            />
            <label htmlFor="positionLeft">{t('settingsPage.left')}</label>
          </div>
          <div className="entry-item__input-and-label">
            <input
              className="entry-control"
              type="radio"
              id="positionRight"
              checked={currencyPosition === 'right'}
              onChange={() => setCurrencyPosition('right')}
            />
            <label htmlFor="positionRight">{t('settingsPage.right')}</label>
          </div>
        </div>

        <div className="entry-item entry-item__full-width">
          <label htmlFor="serverIp" className="entry-label">{t('settingsPage.serverIp')}</label>
          <input
            type="text"
            id="serverIp"
            className="entry-control"
            value={serverIp}
            onChange={(e) => setServerIp(e.target.value)}
          />
        </div>

        <div className="entry-item entry-item__full-width">
          <label htmlFor="cimcoApiBaseUrl" className="entry-label">{t('settingsPage.cimcoApiBaseUrl')}</label>
          <input
            type="text"
            id="cimcoApiBaseUrl"
            className="entry-control"
            value={cimcoApiBaseUrl}
            onChange={(e) => setCimcoApiBaseUrl(e.target.value)}
          />
        </div>

        <div className="entry-item entry-item__content-width">
          <label htmlFor="timezone" className="entry-label">{t('settingsPage.timezone')}</label>
          <Select
            id="timezone"
            options={timezones}
            className="entry-select"
            classNamePrefix="entry-select"
            onChange={(selectedOption) => setTimezone(selectedOption.value)}
            value={timezones.find(tz => tz.value === timezone)}
            isSearchable
          />
        </div>

        <div className="entry-item entry-item__full-width">
          <label htmlFor="companyLogo" className="entry-label">
            {t('settingsPage.uploadCompanyLogo')} (PNG, 1200x400)
          </label>

          <div className="file-upload">
            {/* Hidden file input */}
            <input 
              type="file" 
              id="companyLogo" 
              className="file-upload__input" 
              accept="image/png" 
              onChange={(e) => handleLogoUpload(e)}
              style={{ display: "none" }} // Hide default input
            />

            {/* Custom button that triggers file input */}
            <button 
              type="button" 
              className="button--icon button--grey"
              onClick={() => document.getElementById("companyLogo").click()} // Trigger file input click
            >
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                viewBox="0 0 29 29" 
                width="20" 
                height="20"
              >
                <polygon 
                  points="21,14 15,14 15,8 14,8 14,14 8,14 8,15 14,15 14,21 15,21 15,15 21,15"
                  fill="black"
                />
                <rect width="29" height="29" fill="none"/>
              </svg>
            </button>
          </div>
        </div>



        <img src={`${apiBaseUrl}/mnt/api/logo`} 
          alt="Company Logo" 
          style={{ maxWidth: "300px", marginTop: "10px" }} 
       />


      </div>

      <button className="button--blue" onClick={handleSave}>
        {t('settingsPage.save')}
      </button>

    </>

  );

  const renderFieldSettings = () => (

    <>

      <div className="form-section">

        <div className="entry-item">
          <label htmlFor="requiredFields" className="entry-label">{t('settingsPage.requiredFields')}</label>
          <div className="entry-item__input-and-label">
            <input
              className="entry-control"
              type="checkbox"
              id="machine"
              checked={requiredFields.machine}
              onChange={(e) => setRequiredFields({ ...requiredFields, machine: e.target.checked })}
            />
            <label htmlFor="machine">{t('settingsPage.machine')}</label>
          </div>
          <div className="entry-item__input-and-label">
            <input
              className="entry-control"
              type="checkbox"
              id="fault"
              checked={requiredFields.fault}
              onChange={(e) => setRequiredFields({ ...requiredFields, fault: e.target.checked })}
            />
            <label htmlFor="fault">{t('settingsPage.fault')}</label>
          </div>
          <div className="entry-item__input-and-label">
            <input
              className="entry-control"
              type="checkbox"
              id="technician"
              checked={requiredFields.technician}
              onChange={(e) => setRequiredFields({ ...requiredFields, technician: e.target.checked })}
            />
            <label htmlFor="technician">{t('settingsPage.technician')}</label>
          </div>
        </div>

      </div>

      <button className="button--blue" onClick={handleSave}>
        {t('settingsPage.save')}
      </button>

    </>
  );

  const renderAdvancedSettings = () => (

    <>

      <div className="form-section">

        <div className="entry-item entry-item__full-width">
          <label htmlFor="licenseKey" className="entry-label">{t('settingsPage.LicenseKey')}</label>
          <input
          type="text"
          id="licenseKey"
          className="entry-control"
          // Display the masked value if the license key hasn't been changed.
          value={
            licenseKey && licenseKey === originalLicenseKey
              ? maskLicense(licenseKey)
              : licenseKey
          }
          // On focus, if the key is still unchanged, clear the field so the user must re-enter it.
          onFocus={() => {
            if (licenseKey === originalLicenseKey) {
              setLicenseKey('');
            }
          }}
          // On blur, if the field is left empty, revert to the original key.
          onBlur={() => {
            if (!licenseKey) {
              setLicenseKey(originalLicenseKey);
            }
          }}
          onChange={(e) => setLicenseKey(e.target.value)}
        />
        </div>

{licenseInfoValid && (

        <>

          <div className="entry-item entry-item__full-width">
            <label htmlFor="valid" className="entry-label">{t('settingsPage.LicenseValidUntil')}</label>
            <input
              type="text"
              id="valid"
              className="entry-control"
              value={licenseInfoValid.f1 === false ? "Lifetime" : licenseInfoValid.expirationDate}
              disabled
            />
          </div>

          <div>{licenseInfoValid.f2 === true ? "Trial License" : ""}</div>

          <div className="entry-item entry-item__full-width">
            <label htmlFor="valid" className="entry-label">{t('settingsPage.FeaturesIncluded')}</label>
            <input
              type="text"
              id="valid"
              className="entry-control"
              value={licenseInfoValid?.features?.join(', ') || ''}
              disabled
            />
          </div>

          <div className="entry-item entry-item__medium">
            <label htmlFor="valid" className="entry-label">{t('settingsPage.LicensedToCompany')}</label>
            <input
              type="text"
              id="valid"
              className="entry-control"
              value={licenseInfoValid.customer?.companyName || licenseInfoValid.customer?.name || ''}
              disabled
            />
          </div>

          <div className="entry-item entry-item__small">
            <label htmlFor="valid" className="entry-label">{t('settingsPage.AllowedSimultaneousUsers')}</label>
            <input
              type="text"
              id="valid"
              className="entry-control"
              value={licenseInfoValid.maxNoOfMachines}
              disabled
            />
          </div>
        
        </>

        )}

        <div className="entry-item entry-item__small">
          <label htmlFor="hourlyRate" className="entry-label">{t('settingsPage.internalHourlyRate')}</label>
          <input
            type="number"
            id="hourlyRate"
            className="entry-control"
            value={hourlyRate}
            onChange={(e) => setHourlyRate(parseFloat(e.target.value))}
          />
        </div>

        <div className="entry-item entry-item__full-width">
          <label htmlFor="uploadPath" className="entry-label">{t('settingsPage.uploadPath')}</label>
          <input
            type="text"
            id="uploadPath"
            className="entry-control"
            value={uploadPath}
            onChange={(e) => setUploadPath(e.target.value.replace(/\\/g, '/'))}
          />
        </div>

        <div className="entry-item entry-item__small">
          <label htmlFor="upcomingStatusTimeDays" className="entry-label">{t('settingsPage.upcomingStatusTimeDays')}</label>
          <input
            type="number"
            id="upcomingStatusTimeDays"
            className="entry-control"
            value={upcomingStatusTimeDays}
            onChange={(e) => setUpcomingStatusTimeDays(parseInt(e.target.value))}
          />
        </div>
        <div className="entry-item entry-item__full-width">
          <label htmlFor="dbPass" className="entry-label">{t('settingsPage.dbPass')}</label>
          <input
            type="password"
            id="dbPass"
            className="entry-control"
            value={dbPass}
            onChange={(e) => setDbPass(e.target.value)}
            placeholder={t('settingsPage.dbPassPlaceholder')}
          />
        </div>
        <div className="entry-item entry-item__full-width">
          <label htmlFor="registrationCode" className="entry-label">{t('settingsPage.registrationCode')}</label>
          <input
            type="text"
            id="registrationCode"
            className="entry-control"
            value={registrationCode}
            onChange={(e) => setRegistrationCode(e.target.value)}
          />
        </div>
        <div className="entry-item entry-item__full-width">
          <label htmlFor="maxFileSizeMB" className="entry-label">{t('settingsPage.maxFileSizeTitle')}</label>
          <input
            type="number"
            id="maxFileSizeMB"
            className="entry-control"
            min = "10"
            Max = "256"
            value={maxFileSizeMB}
            onChange={(e) => setMaxFileSizeMB(e.target.value)}
            placeholder={t('settingsPage.maxFileSizeMB')}
          />
        </div>
        <div className="entry-item entry-item__full-width">
          <label className="entry-label">{t('settingsPage.currentVersion')}</label>
          <div className="entry-control">
            <strong>{versionData?.currentVersion || 'N/A'}</strong>
          </div>

          <button
            type="button"
            className="button--grey"
            onClick={toggleShowAllVersions}
            style={{ marginTop: '10px' }}
          >
            {showAllVersions ? t('settingsPage.hideVersionInfo') : t('settingsPage.showVersionInfo')}
          </button>

          {showAllVersions && Array.isArray(versionData?.releases) && (
            <>
              {versionData.releases.map((release) => (
                <div key={release.version} className="entry-item entry-item__full-width">
                  <label className="entry-label">
                    {t('settingsPage.version')} {release.version} - {t('settingsPage.releasedOn')} {release.releaseDate}
                  </label>
                  <div
                    className="entry-control"
                    style={{ padding: '1rem', whiteSpace: 'pre-wrap' }}
                  >
                    {release.changeLog}
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </div>

      <button className="button--blue" onClick={handleSave}>
        {t('settingsPage.save')}
      </button>

    </>

  );

  const renderCategorySettings = () => (
	
    <>

      <div className="form-section">

        <label htmlFor="categories" className="entry-label">{t('settingsPage.addCategory')}</label>

        <div className="entry-item entry-item__settings-category">
          <input
            type="text"
            value={newCategory}
            onChange={(e) => setNewCategory(e.target.value)}
            className="entry-control"
          />

          <button type="button" className="button--icon button--grey" onClick={handleAddCategory}>
            <AddIcon />
          </button>
        </div>

        <div className="form-section--inner-divider"></div>

        <label htmlFor="categories" className="entry-label">{t('settingsPage.manageCategories')}</label>

        {categories.map((category, index) => (
          <div key={index} className="entry-item entry-item__settings-category">
			
            {index === editIndex ? (
              <input type="text" value={editCategory} onChange={(e) => setEditCategory(e.target.value)} className="entry-control" />
            ) : (
              <input type="text" value={category} className="entry-control" disabled />
            )}

            <div className="button-options">
              {index === editIndex ? (
                <button className="button--icon" onClick={handleUpdateCategory}>{t('settingsPage.save')}</button>
              ) : (
                <>
                  <button className="slim-button" type="button" onClick={() => handleEditCategory(index)}>
                    <EditItemIcon />
                  </button>
                  <button className="slim-button--remove" type="button" onClick={() => handleDeleteCategory(index)}>
                    <RemoveItemIcon />
                  </button>
                </>
              )}
            </div>

          </div>
        ))}

      </div>
      
      <button className="button--blue" onClick={handleSave}>
        {t('settingsPage.save')}
      </button>

    </>

    

  );

  // Render email settings
  const renderEmailSettings = () => (

    <>

      <div className="form-section">
        <div className="entry-item entry-item__full-width">
          <label htmlFor="emailHost" className="entry-label">{t('settingsPage.emailHost')}</label>
          <input
            type="text"
            id="emailHost"
            className="entry-control"
            value={emailHost}
            onChange={(e) => setEmailHost(e.target.value)}
          />
        </div>
        <div className="entry-item entry-item__small">
          <label htmlFor="emailPort" className="entry-label">{t('settingsPage.emailPort')}</label>
          <input
            type="number"
            id="emailPort"
            className="entry-control"
            value={emailPort}
            onChange={(e) => setEmailPort(parseInt(e.target.value))}
          />
        </div>
        <div className="entry-item entry-item__medium">
          <label htmlFor="emailUser" className="entry-label">{t('settingsPage.emailUser')}</label>
          <input
            type="text"
            id="emailUser"
            className="entry-control"
            value={emailUser}
            onChange={(e) => setEmailUser(e.target.value)}
          />
        </div>
        <div className="entry-item entry-item__medium">
          <label htmlFor="emailPass" className="entry-label">{t('settingsPage.emailPass')}</label>
          <input
            type="password"
            id="emailPass"
            className="entry-control"
            value={emailPass}
            onChange={(e) => setEmailPass(e.target.value)}
          />
        </div>

      </div>
      <div className="form-section">
      <h3 className="entry-label">Email Presets</h3>
{emailPresets.map((preset, index) => (
  <div key={preset.id} className="entry-item entry-item__full-width">
    <label className="entry-label">{preset.title}</label>

    {/* Insert Buttons */}
    <div className="entry-item__insert-buttons" style={{ marginBottom: '0.5rem' }}>
  <button type="button" className="button--grey" onClick={() => insertLink(index)}>Insert Link</button>
  {getPresetVariables(preset.id).map((variable) => (
    <button
      key={variable}
      type="button"
      className="button--grey"
      onClick={() => insertVariable(variable, index)}
    >
      Insert {variable}
    </button>
  ))}
</div>

    {/* Email Body Textarea */}
    <textarea
  ref={(el) => (textareaRefs.current[index] = el)}
  className="entry-control"
  rows={4}
  value={preset.body}
  onChange={(e) => {
    const updated = [...emailPresets];
    updated[index].body = e.target.value;
    setEmailPresets(updated);
  }}
  placeholder={`Enter content for ${preset.title}`}
/>

  </div>
))}

</div>
      <button className="button--blue" onClick={handleSaveEmailSettings}>
        {t('settingsPage.save')}
      </button>

    </>

  );

  // Render License Information tab
  const renderLicenseInformation = () => {
    if (!licenseKey) {
      return <div>{t('settingsPage.enterLicenseKeyFirst')}</div>;
    }
    if (!licenseInfo) {
      return <div>{t('settingsPage.loadingLicenseInfo')}</div>;
    }
    // Array of feature flag keys to check
    const featureFlags = ['f1', 'f2', 'f3', 'f4', 'f5', 'f6', 'f7', 'f8'];
  
    return (
      <div className="form-section">
        <div className="license-info">
          <div className="entry-item">
            <label className="entry-label">{t('settingsPage.licenseKeyLabel')}:</label>
            <span>{maskLicense(licenseInfo.key)}</span>
          </div>
          <div className="entry-item">
            <label className="entry-label">{t('settingsPage.licenseType')}:</label>
            <span>{licenseInfo.f1 ? t('settingsPage.subscription') : t('settingsPage.perpetual')}</span>
          </div>
          <div className="entry-item">
            <label className="entry-label">{t('settingsPage.created')}:</label>
            <span>{moment(licenseInfo.created).format('YYYY-MM-DD HH:mm:ss')}</span>
          </div>
          <div className="entry-item">
            <label className="entry-label">{t('settingsPage.expires')}:</label>
            <span>{moment(licenseInfo.expires).format('YYYY-MM-DD HH:mm:ss')}</span>
          </div>
          <div className="entry-item">
            <label className="entry-label">{t('settingsPage.period')}:</label>
            <span>{licenseInfo.period} {t('settingsPage.days')}</span>
          </div>
          <div className="entry-item">
            <label className="entry-label">{t('settingsPage.customer')}:</label>
            <span>{licenseInfo.customer?.companyName || licenseInfo.customer?.name || ''}</span>
          </div>
          <div className="entry-item">
            <label className="entry-label">{t('settingsPage.allowedMachines')}:</label>
            <span>{licenseInfo.f5 ? t('settingsPage.unlimited') : licenseInfo.maxNoOfMachines}</span>
          </div>
          {/* Feature flags: display only those that are true */}
          <div className="license-flags">
            {featureFlags.map(flag => 
              licenseInfo[flag] && (
                <div className="entry-item" key={flag}>
                  <label className="entry-label">{t(`settingsPage.${flag}`)}</label>
                </div>
              )
            )}
          </div>
        </div>
      </div>  
    );
  };
  

  return (
    <>

      <div className="content">

        <div className="top-menu"></div>

        <div className="settings-container">
          <div className="settings-title">{settingsTitle}</div>

          {/* Mobile Dropdown */}
          <div className="settings-menu-mobile">
            <select
              className="settings-menu-mobile__select"
              id="menu-select"
              value={activeTab}
              onChange={handleSelectChange}
            >
              {optionList.map(({ key, label }) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
          </div>

          {/* Desktop Button Menu */}
          <div className="settings-menu">
            {optionList.map(({ key, label }) => (
              <button
                key={key}
                type="button"
                className={activeTab === key ? "button-class-selected" : "button-class"}
                onClick={() => handleMenuButtonClick(key, label)}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="settings-options">
		  
							   
            {activeTab === 'general' && renderGeneralSettings()}
            {activeTab === 'fields' && renderFieldSettings()}
            {activeTab === 'advanced' && renderAdvancedSettings()}
            {activeTab === 'categories' && renderCategorySettings()}
            {activeTab === 'editRights' && <EditRights />}
            {activeTab === 'email' && renderEmailSettings()}
            {activeTab === 'license' && renderLicenseInformation()}
          </div>
        </div>

      </div>

      {/* Modal for Notifications */}
      <Modal
        isOpen={modalIsOpen}
        onRequestClose={() => setModalIsOpen(false)}
        contentLabel={t('settingsPage.notification')}
        className="notification-modal"
        overlayClassName="notification-overlay"
      >
        <h2>{t('settingsPage.notification')}</h2>
        <p>{modalMessage}</p>
        <button onClick={() => setModalIsOpen(false)}>{t('settingsPage.close')}</button>
      </Modal>

    </>

  );
};

export default Settings;
